import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { imgUrl, fmtDate, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { TableToolbar, matches, exportRows, Empty } from "./shared";

const COLUMNS = [
  { label: "Submitted", value: (u) => fmtDate(u.kyc?.submitted_at) },
  { label: "Name", value: "name" },
  { label: "Email", value: "email" },
  { label: "Phone", value: "phone" },
  { label: "PAN", value: (u) => u.kyc?.pan_number },
  { label: "Aadhaar (last 4)", value: (u) => u.kyc?.aadhaar_last4 },
  { label: "Status", value: (u) => u.kyc?.status },
  { label: "Reviewed By", value: (u) => u.kyc?.reviewed_by },
  { label: "Note", value: (u) => u.kyc?.note },
];
const STYLE = { pending: "text-[#D4AF37] border-[#D4AF37]/40", verified: "text-emerald-400 border-emerald-400/40", rejected: "text-red-400 border-red-400/40" };

const KycDialog = ({ u, onClose, onDone }) => {
  const [note, setNote] = useState(u?.kyc?.note || "");
  const [busy, setBusy] = useState(false);
  if (!u) return null;
  const act = async (status) => {
    setBusy(true);
    try {
      await api.patch(`/admin/kyc/${u.id}`, { status, note });
      toast.success(status === "verified" ? "KYC verified — investor notified" : `KYC ${status} — investor notified`);
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const btn = "px-5 py-3 font-accent text-[10px] tracking-[0.2em] uppercase border transition-colors disabled:opacity-50";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl w-[95vw] max-h-[90vh] overflow-y-auto bg-[#0A0A0A] border-[#D4AF37]/30 text-white p-8 rounded-none" data-testid="kyc-dialog">
        <DialogHeader className="text-left">
          <p className="font-accent text-[10px] tracking-[0.25em] uppercase text-[#D4AF37]">KYC review</p>
          <DialogTitle className="font-display text-2xl tracking-tight text-white">{u.name}</DialogTitle>
          <DialogDescription className="text-white/50 text-sm">{u.email} · {u.phone || "no phone"} · PAN <span className="font-mono text-white">{u.kyc.pan_number}</span> · Aadhaar ····{u.kyc.aadhaar_last4}</DialogDescription>
        </DialogHeader>
        <div className="mt-6 grid md:grid-cols-2 gap-4">
          {[["PAN card", u.kyc.pan_image, "kyc-pan-image"], ["Aadhaar card", u.kyc.aadhaar_image, "kyc-aadhaar-image"]].map(([label, path, tid]) => (
            <figure key={tid} className="bg-black border border-white/10">
              <a href={imgUrl(path)} target="_blank" rel="noopener noreferrer"><img src={imgUrl(path)} alt={label} className="w-full max-h-[50vh] object-contain" data-testid={tid} /></a>
              <figcaption className="px-3 py-2 font-accent text-[9px] tracking-[0.2em] uppercase text-white/40">{label} · click to open full size</figcaption>
            </figure>
          ))}
        </div>
        <textarea data-testid="kyc-note-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note to investor (shown on rejection, optional)" className="mt-6 w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 gold-focus" />
        <div className="mt-5 flex flex-wrap gap-3">
          <button data-testid="kyc-verify-button" disabled={busy} onClick={() => act("verified")} className={`${btn} border-emerald-400 text-emerald-400 hover:bg-emerald-400 hover:text-black`}>Verify</button>
          <button data-testid="kyc-reject-button" disabled={busy} onClick={() => act("rejected")} className={`${btn} border-red-400 text-red-400 hover:bg-red-400 hover:text-black`}>Reject</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const KycTab = ({ onChanged }) => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("pending");
  const [review, setReview] = useState(null);
  const load = () => api.get("/admin/kyc").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const rows = items.filter((u) => (filter === "all" || u.kyc?.status === filter) && matches({ ...u, pan: u.kyc?.pan_number }, q));
  return (
    <div data-testid="admin-kyc-tab">
      <TableToolbar title="KYC Verification" count={rows.length} query={q} onQuery={setQ} placeholder="Search name, PAN, email…" testId="kyc" onExport={() => exportRows("kyc", COLUMNS, rows)}>
        <div className="flex border border-white/10" data-testid="kyc-status-filter">
          {["pending", "verified", "rejected", "all"].map((f) => (
            <button key={f} type="button" data-testid={`kyc-filter-${f}`} onClick={() => setFilter(f)} className={`px-3 py-2 font-accent text-[9px] tracking-[0.2em] uppercase transition-colors ${filter === f ? "bg-[#D4AF37] text-black" : "text-white/50 hover:text-white"}`}>{f}</button>
          ))}
        </div>
      </TableToolbar>
      <div className="border border-white/10">
        <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/10 font-accent text-[10px] tracking-[0.2em] uppercase text-white/35">
          <span className="col-span-4">Investor</span><span className="col-span-3">PAN / Aadhaar</span><span className="col-span-2">Submitted</span><span className="col-span-1">Status</span><span className="col-span-2 text-right">Action</span>
        </div>
        {rows.map((u) => (
          <div key={u.id} className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/10 last:border-0 items-center" data-testid={`kyc-row-${u.id}`}>
            <div className="col-span-4 min-w-0"><p className="text-sm text-white truncate">{u.name}</p><p className="text-xs text-white/35 truncate">{u.email}</p></div>
            <div className="col-span-3 text-xs font-mono text-white/70">{u.kyc.pan_number} · ····{u.kyc.aadhaar_last4}</div>
            <span className="col-span-2 text-xs text-white/50">{fmtDate(u.kyc.submitted_at)}</span>
            <span className={`col-span-1 font-accent text-[9px] tracking-[0.15em] uppercase border px-2 py-1 text-center ${STYLE[u.kyc.status] || ""}`} data-testid={`kyc-status-${u.id}`}>{u.kyc.status}</span>
            <div className="col-span-2 flex justify-end">
              <button data-testid={`kyc-review-button-${u.id}`} onClick={() => setReview(u)} className="px-4 py-2 font-accent text-[9px] tracking-[0.2em] uppercase border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-colors">Review</button>
            </div>
          </div>
        ))}
        {rows.length === 0 && <Empty testId="kyc-empty" text="No KYC submissions match." />}
      </div>
      <KycDialog key={review?.id} u={review} onClose={() => setReview(null)} onDone={() => { setReview(null); load(); onChanged?.(); }} />
    </div>
  );
};
